
import { Component } from "@theme/component";
import { DialogComponent } from "@theme/dialog";
import { bindCloseCursor } from '@theme/custom-cursor';
import { DialogCloseEvent } from '@theme/events';
import {
  isClickedOutside,
  onAnimationEnd,
  onIdleAfterDOMReady,
  debounce,
  throttle,
  getHeaderMobileBreakpointWidth,
} from '@theme/utilities';
import { ThemeEvents, MenuDropdownOpenEvent, MenuDropdownCloseEvent, DropdownDialogOpenEvent, DropdownDialogCloseEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';

/**
 * Header custom elements (kept in one module = one request; see CLAUDE.md notes on the no-bundler setup).
 * Defines: <header-component>, <header-navigation>, <details is="menu-dropdown">,
 * <details is="mega-dropdown">, <mobile-menu-component>,
 * <details is="mobile-menu-details">, <height-observer>.
 * (<announcement-bar> lives in announcement-bar.js, loaded by its own section.)
 */

/* <header-component> — sticky scroll behaviour + dropdown/menu coordination */
class HeaderComponent extends Component {


  /** @type {number} */
  #mobileBreakpoint = 900;

  /** @type {number} */
  #openDropdownCount = 0;

  /** @type {number} */
  #dropdownCloseTimeout = null;


  /** @type {Function|null} */
  #scrollHandler = null;

  /** @type {Function|null} */
  #handleDropdownOpenDelegated = null;

  /** @type {Function|null} */
  #handleDropdownCloseDelegated = null;

  /** @type {number|null} */
  #animationTimeout = null;

  /** @type {number|null} */
  #animateFastTimeout = null;

  /** @type {number|null} */
  #dropdownOpenDelayTimeout = null;

  #scrollDir = false;
  #scrollDirAnchor = 0;
  static #SCROLL_DIRECTION_HYSTERESIS = 5;

  /** @type {boolean} True once the header has taken over the top of the viewport */
  #stickyEngaged = false;

  /** @type {boolean} */
  #isMobile = false;

  #rowHeightCache = { top: 0, bottom: 0 };

  /** @type {boolean} */
  #isHeaderTransparent = false;

  /** @type {boolean} Blocks scroll handler after resize to prevent Scroll Anchoring feedback loop */
  #isPostResize = false;

  /** @type {number|null} */
  #postResizeTimer = null;

  /** @type {boolean} Keeps the header collapsed (no pinning) during the filter's programmatic scroll */
  #suppressPin = false;

  /** @type {number|null} */
  #suppressPinTimer = null;

  /** @type {NodeListOf<Element>|Element[]} Zones that collapse the header once they cover the top of the viewport */
  #hideZones = [];

  /** @type {boolean} Keeps the header pinned while a block in this section is selected in the editor */
  #editorBlockSelected = false;

  /** @type {number|null} Defers the initial `.header-sticky` add for boxed headers */
  #stickyClassTimer = null;

  /** @type {number} Last known scrollY, used for scroll-direction detection */
  currentTop = 0;

  /** @type {{top: number, header: number}} Where the header rests in the document, and its height */
  #bounds = { top: 0, header: 0 };


  connectedCallback() {
    super.connectedCallback();

    if (!this.headerSection) {
      return;
    }


    this.currentTop = 0;
    this.#setHeaderTransparent();
    this.#updateHeaderBounds();
    this.#updateRowHeightVars();
    this.#mobileBreakpoint = getHeaderMobileBreakpointWidth();
    this.#isMobile = window.innerWidth < this.#mobileBreakpoint;

    this._handleResize = debounce(() => {
      const wasMobile = this.#isMobile;
      this.#isMobile = window.innerWidth < this.#mobileBreakpoint;
      this.#updateHeaderBounds();
      this.#updateRowHeightVars();
      this.#isPostResize = true;
      clearTimeout(this.#postResizeTimer);
      this.#onScroll(true);
      this.#postResizeTimer = setTimeout(() => {
        this.#isPostResize = false;
        this.#onScroll(true);
      }, 300);
      // Load appropriate menu when breakpoint changes
      if (wasMobile !== this.#isMobile) {
        this.#handleHeaderMenuLoaded();
      }
    }, 150);
    window.addEventListener('resize', this._handleResize);

    this.#scrollHandler = throttle(() => {
      // Only the instance matching the current breakpoint does scroll work.
      if ((this.headerDesktop && this.#isMobile) || (this.headerMobile && !this.#isMobile)) return;
      // While suppressed (after a filter), re-arm the idle clear each frame so the
      // header stays collapsed until the programmatic smooth scroll actually stops.
      if (this.#suppressPin) this.#armSuppressPinClear(150);
      if (this.#isPostResize) return;
      this.#onScroll(false);
    });
    window.addEventListener('scroll', this.#scrollHandler, { passive: true });
    this.#onScroll(true);
    this.#updateStickyClass();
    this.#collectHideZones();

    document.addEventListener(MenuDropdownOpenEvent.eventName, this.#handleDropdownOpen);
    document.addEventListener(MenuDropdownCloseEvent.eventName, this.#handleDropdownClose);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    this.#handleDropdownOpenDelegated = (event) => {
      const target = event.target.closest('dropdown-dialog, search-bar-component');
      if (target && this.contains(target)) {
        this.#handleDropdownOpen(event);
      }
    };

    this.#handleDropdownCloseDelegated = (event) => {
      const target = event.target.closest('dropdown-dialog, search-bar-component');
      if (target && this.contains(target)) {
        this.#handleDropdownClose(event);
      }
    };

    this.addEventListener(DropdownDialogOpenEvent.eventName, this.#handleDropdownOpenDelegated);
    this.addEventListener(DropdownDialogCloseEvent.eventName, this.#handleDropdownCloseDelegated);


    if (Shopify.designMode) {
      this.closest('height-observer')?.refresh?.();
      document.addEventListener('shopify:block:select', this.#handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);
      document.addEventListener('shopify:section:load', this.#handleSectionLoad);
    }

    if (!Shopify.designMode) {
      onIdleAfterDOMReady(() => this.#handleHeaderMenuLoaded());

      // Fallback: load on first interaction in case idle never fired or component is mobile
      this.addEventListener('pointerenter', this.#handleHeaderMenuLoaded);
      this.addEventListener('focusin', this.#handleHeaderMenuLoaded);
      this.addEventListener('touchstart', this.#handleHeaderMenuLoaded, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this._handleResize);

    // Cleanup all event listeners
    if (Shopify.designMode) {
      document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);
      document.removeEventListener('shopify:section:load', this.#handleSectionLoad);
    }

    if (this.#scrollHandler) {
      this.#scrollHandler.cancel?.();
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }
    document.removeEventListener(MenuDropdownOpenEvent.eventName, this.#handleDropdownOpen);
    document.removeEventListener(MenuDropdownCloseEvent.eventName, this.#handleDropdownClose);
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    this.#hideZones = [];
    clearTimeout(this.#suppressPinTimer);
    this.#suppressPinTimer = null;
    clearTimeout(this.#stickyClassTimer);
    this.#stickyClassTimer = null;

    this.removeEventListener(DropdownDialogOpenEvent.eventName, this.#handleDropdownOpenDelegated);
    this.removeEventListener(DropdownDialogCloseEvent.eventName, this.#handleDropdownCloseDelegated);

    if (!Shopify.designMode) {
      this.#removeMenuLoadListeners();
    }

    // Cleanup timeouts
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
      this.#animationTimeout = null;
    }

    if (this.#animateFastTimeout) {
      clearTimeout(this.#animateFastTimeout);
      this.#animateFastTimeout = null;
    }

    if (this.#dropdownOpenDelayTimeout) {
      clearTimeout(this.#dropdownOpenDelayTimeout);
      this.#dropdownOpenDelayTimeout = null;
    }

  }

  #setHeaderTransparent = () => {
    this.#isHeaderTransparent = !!document.querySelector('.content-for-layout .shopify-section:first-child .allow-transparent-header');
    if (this.#isHeaderTransparent) {
      this.classList.add('header--transparent');
    } else {
      this.classList.remove('header--transparent');
    }
  }

  /**
   * Caches the geometry the scroll handler needs, so no scroll frame reads layout.
   * The resting place comes from summing what sits above, not from offsetTop: that reads 0
   * once the section is positioned, and a stuck section reports its pinned position.
   * Heights hold still either way.
   */
  #updateHeaderBounds = () => {
    let above = 0;
    for (let el = this.headerSection?.previousElementSibling; el; el = el.previousElementSibling) {
      above += el.offsetHeight;
    }
    this.#bounds.top = above;
    this.#bounds.header = this.offsetHeight;
  }

  /**
   * Updates the sticky class based on stickyType
   */
  #updateStickyClass = () => {
    if (!this.headerSection) return;
    const isSticky = this.stickyType != 'none';

    if (isSticky) {
      this.headerSection.classList.add('header-sticky--' + this.stickyType);
      this.headerSection.classList.add('header-sticky--no-animation');
      if (this.#animationTimeout) {
        clearTimeout(this.#animationTimeout);
      }
      this.#animationTimeout = setTimeout(() => {
        if (this.headerSection) {
          this.headerSection.classList.remove('header-sticky--no-animation');
        }
        this.#animationTimeout = null;
      }, 500);
    }
  }

  /**
   * Handles Shopify theme editor block select event
   * Only updates if the event is related to this component's section
   */
  #handleBlockSelect = (e) => {
    // Check if the event target is within this component's section
    if (!e.target || !this.headerSection?.contains(e.target)) return;
    this.#editorBlockSelected = true;
    if (this.headerSection.classList.contains('header-scrolling')) {
      this.headerSection.classList.add('header-pinning', 'header-revealed');
    }
    this.#updateStickyClass();
  }

  /**
   * Handles Shopify theme editor block deselect event
   * Clears the flag so normal sticky behaviour resumes on the next scroll,
   * but leaves the header pinned as-is (no forced collapse)
   */
  #handleBlockDeselect = (e) => {
    if (!e.target || !this.headerSection?.contains(e.target)) return;
    this.#editorBlockSelected = false;
  }


  /**
   * Handles scroll events for sticky header behavior
   * Manages different sticky types: 'scroll_up', 'always', or 'none'
   */
  #onScroll= (initial = false) => {
    if(!this.headerSection) return;
    if (this.headerDesktop && this.#isMobile) return;
    if (this.headerMobile && !this.#isMobile) return;

    let zonesStuck = false;
    for (const zone of this.#hideZones) {
      const box = zone.parentElement ?? zone;
      const { top, bottom } = box.getBoundingClientRect();
      const stuck = top <= 0 && bottom > 0;
      zone.classList.toggle('hide-zone-stuck', stuck);
      zonesStuck ||= stuck;
    }

    const scrollTop = Math.round(window.scrollY);

    if (this.stickyType == 'none') {
      this.currentTop = scrollTop;
      return;
    }

    const rawUp = scrollTop < this.currentTop;
    if (rawUp !== this.#scrollDir) {
      if (Math.abs(scrollTop - this.#scrollDirAnchor) >= HeaderComponent.#SCROLL_DIRECTION_HYSTERESIS) {
        this.#scrollDir = rawUp;
        this.#scrollDirAnchor = scrollTop;
      }
    } else {
      this.#scrollDirAnchor = scrollTop;
    }
    // The hide zone is a mobile-only feature; the desktop header keeps its normal pinning.
    const inHideZone = zonesStuck && this.#isMobile;
    const scrollingUp = (this.#suppressPin || inHideZone) ? false : this.#scrollDir;
    const hidden = inHideZone && !this.#editorBlockSelected;
    this.headerSection.classList.toggle('header-hide-zone', hidden);
    this.headerSection.classList.toggle('header-stuck', scrollTop > this.#bounds.top);
    this.currentTop = scrollTop;

    // No nested rAF: these already run inside the throttle's cancellable one.
    switch (this.stickyType) {
      case 'always': this.#stickyAlways(scrollTop, hidden, initial); break;
      case 'scroll_up': this.#stickyScrollUp(scrollTop, scrollingUp, initial); break;
      case 'top_only': this.#stickyTopOnly(scrollTop, scrollingUp, initial); break;
      case 'bottom_only': this.#stickyBottomOnly(scrollTop, scrollingUp, initial); break;
    }
  }

  /**
   * Re-points the hide zones at whatever `[data-header-hide-zone]` elements exist now.
   * Called on mount and after a section re-render in the editor.
   */
  #collectHideZones = () => {
    this.#hideZones = document.querySelectorAll('[data-header-hide-zone]');
    this.#onScroll(true);
  }

  /**
   * A re-render in the editor can also change what sits above the header — adding an
   * announcement bar, say — which moves every mark the scroll handler compares against.
   */
  #handleSectionLoad = () => {
    this.#updateHeaderBounds();
    this.#collectHideZones();
  }

  /**
   * Handles sticky behavior when only the bottom row stays. CSS sticky pins it; the class
   * only follows, so it waits for the same point — when the bottom row reaches the top.
   */
  #stickyBottomOnly = (scrollTop, scrollingUp, initial = false) => {
    if (!this.querySelector('.header__row--bottom')) return;
    this.#applyStickyState({
      scrollTop, scrollingUp, initial,
      engageAt: this.#bounds.top + this.#rowHeightCache.top,
      inertTarget: this.querySelector('.header__row--top'),
    });
  }

  /** Handles sticky behavior when only the top row stays, collapsing the bottom one. */
  #stickyTopOnly = (scrollTop, scrollingUp, initial = false) => {
    if (!this.querySelector('.header__row--top')) return;
    this.#applyStickyState({
      scrollTop, scrollingUp, initial, defer: true,
      engageAt: this.#bounds.top,
      inertTarget: this.querySelector('.header__row--bottom'),
    });
  }

  /**
   * Handles sticky behavior when the header returns on the way up. It drifts off with the
   * page first, so it takes over only once all of it — announcement bar included — has gone by.
   */
  #stickyScrollUp = (scrollTop, scrollingUp, initial = false) => {
    this.#applyStickyState({
      scrollTop, scrollingUp, initial, latchReveal: true,
      engageAt: this.#bounds.top + this.#bounds.header,
      inertTarget: this,
    });
  }

  /**
   * Shared by the direction-aware modes: the state classes and `inert`. Each mode engages at
   * its own mark but they all hand back at the header's resting place, the one point where
   * pinned and natural positions agree, so letting go never moves anything.
   */
  #applyStickyState = ({ scrollTop, scrollingUp, initial, engageAt, defer = false, latchReveal = false, inertTarget }) => {
    if (defer) this.#applyStickyBase(initial);
    else this.headerSection.classList.add('header-sticky');

    if (scrollTop > engageAt) this.#stickyEngaged = true;
    else if (scrollTop <= this.#bounds.top) this.#stickyEngaged = false;

    const scrolled = this.#stickyEngaged;
    if (scrolled) {
      const pinning = scrollingUp || this.#editorBlockSelected;
      this.headerSection.classList.add('header-scrolling');
      this.headerSection.classList.toggle('header-pinning', pinning);
      if (pinning && latchReveal) this.headerSection.classList.add('header-revealed');
    } else {
      this.headerSection.classList.remove('header-scrolling', 'header-pinning', 'header-revealed');
    }
    inertTarget?.toggleAttribute('inert', scrolled && !scrollingUp && !this.#editorBlockSelected);
  }

  /**
   * Handles sticky behavior when header should always stick
   */
  #stickyAlways = (scrollTop, hidden, initial = false) => {
    if (this.stickyType !== 'always') return;
    // A boxed header needs a little travel before its box is worth showing.
    const boxed = this.classList.contains('header-sticky--boxed');
    const engageAt = boxed ? Math.max(this.#bounds.top, 20) : this.#bounds.top;

    if (scrollTop > engageAt) {
      this.#applyStickyBase(initial);
      this.headerSection.classList.add('header-scrolling', 'header-pinning');
    } else {
      this.headerSection.classList.remove('header-scrolling', 'header-pinning');
      this.headerSection.classList.add('header-sticky');
    }
    this.toggleAttribute('inert', hidden);
  }

  /**
   * Adds `.header-sticky`. On the initial pass the add is deferred ~300ms for
   * boxed headers and for top_only (so the boxed style / row collapse doesn't
   * snap in). Otherwise it's immediate and idempotent, so calling it every
   * scroll frame never spawns timers.
   * @param {boolean} initial - True on mount/resize/filter, false on scroll
   */
  #applyStickyBase(initial) {
    const shouldDefer = initial && (this.classList.contains('header-sticky--boxed') || this.stickyType === 'top_only');
    if (shouldDefer) {
      clearTimeout(this.#stickyClassTimer);
      this.#stickyClassTimer = setTimeout(() => {
        this.headerSection?.classList.add('header-sticky');
        this.#stickyClassTimer = null;
      }, 300);
    } else {
      this.headerSection.classList.add('header-sticky');
    }
  }

  #updateRowVar(selector, cacheKey, varName) {
    const row = this.querySelector(selector);
    if (!row) return;
    const container = row.querySelector('.header__container');
    const height = container ? container.offsetHeight : row.offsetHeight;
    if (height === this.#rowHeightCache[cacheKey]) return;
    this.#rowHeightCache[cacheKey] = height;
    this.style.setProperty(varName, height + 'px');
    this.headerSection?.style.setProperty(varName, height + 'px');
  }

  #updateRowHeightVars() {
    if (this.stickyType !== 'top_only' && this.stickyType !== 'bottom_only') return;
    if (this.offsetParent === null) return;
    this.#updateRowVar('.header__row--top', 'top', '--header-top-height');
    this.#updateRowVar('.header__row--bottom', 'bottom', '--header-bottom-height');
  }

  /**
   * Collapses the header after a collection/search filter and keeps it collapsed
   * during the filter's programmatic smooth scroll. Without suppression the
   * scroll-up motion would pin the header (re-expanding top_only/bottom_only or
   * sliding scroll_up back in). Normal pinning resumes once the scroll settles.
   */
  #handleFilterUpdate = () => {
    if (this.stickyType === 'none' || this.stickyType === 'always') return;

    this.#suppressPin = true;
    this.headerSection?.classList.remove('header-pinning');
    this.#onScroll(true);

    this.#armSuppressPinClear(300);
  }

  #armSuppressPinClear = (delay) => {
    clearTimeout(this.#suppressPinTimer);
    this.#suppressPinTimer = setTimeout(this.#clearSuppressPin, delay);
  }

  #clearSuppressPin = () => {
    this.#suppressPin = false;
    this.#suppressPinTimer = null;
  }

  /**
   * Loads menu content based on screen size
   * Desktop: loads mega dropdown, Mobile: loads mobile menu
   */
  #handleHeaderMenuLoaded = () => {
    // Remove interaction listeners immediately to prevent re-entry during async morph
    this.#removeMenuLoadListeners();

    if (!this.#isMobile) {
      this.#handleMegaDropdownLoaded();
    } else {
      this.#handleMobileMenuLoaded();
    }
  }

  /**
   * Loads mega dropdown menu content lazily via Section Rendering API.
   * Uses /?view=menu-loader to fetch menu structure without polluting search analytics.
   */
  #handleMegaDropdownLoaded = async () => {
    const navigations = this.querySelectorAll('header-navigation');

    await Promise.all([...navigations].map((headerNavigation) => {
      if (headerNavigation.hasAttribute('data-submenu-loaded') || headerNavigation.hasAttribute('data-submenu-loading')) return null;
      if (typeof headerNavigation.handleMegaDropdownLoaded !== 'function') return null;
      return headerNavigation.handleMegaDropdownLoaded();
    }));
  }

  /**
   * Loads mobile menu content lazily via Section Rendering API.
   * Uses /?view=menu-loader to fetch menu structure without polluting search analytics.
   */
  #handleMobileMenuLoaded = () => {
    const mobileMenuComponent = document.querySelector('mobile-menu-component');

    if (!mobileMenuComponent || mobileMenuComponent.hasAttribute('data-mobile-menu-loaded') || mobileMenuComponent.hasAttribute('data-mobile-menu-loading')) return;
    if (typeof mobileMenuComponent.handleMobileMenuLoaded === 'function') {
      mobileMenuComponent.handleMobileMenuLoaded();
    }
  }

  /**
   * Removes interaction-based preload listeners.
   */
  #removeMenuLoadListeners = () => {
    this.removeEventListener('pointerenter', this.#handleHeaderMenuLoaded);
    this.removeEventListener('focusin', this.#handleHeaderMenuLoaded);
    this.removeEventListener('touchstart', this.#handleHeaderMenuLoaded);
  }

  /**
   * Handles dropdown open event
   */
  #handleDropdownOpen = (event) => {

    // Cancel pending remove so rapid open→close→open doesn't strip the class while open
    if (this.#animateFastTimeout) {
      clearTimeout(this.#animateFastTimeout);
      this.#animateFastTimeout = null;
    }
    this.classList.add('header--open-dropdown-menu');
    if (this.headerTransparent) {
      this.#openDropdownCount++;

      if (this.#dropdownCloseTimeout) {
        clearTimeout(this.#dropdownCloseTimeout);
        this.#dropdownCloseTimeout = null;
      }
      // Always clear closing class when any dropdown opens, regardless of event target type
      this.classList.remove('header--dropdown-menu-closing');
      if(event.target.classList.contains('menu-dropdown') || event.target.classList.contains('dropdown-dialog-component') || event.target.matches('search-bar-component')) {
          this.style.setProperty('--mega-menu-height', '0px');
          this.classList.remove('header--mega-menu-active', 'header--mega-menu-closing', 'header--dropdown-menu-closing', 'header--mega-from-top');
          this.classList.add('header--mega-menu-open');

          if (event.target.classList.contains('menu-dropdown') && !this.headerSection?.classList.contains('header-scrolling')) {
            this.classList.add('header--transparent-dropdown-opening');
            if (this.#dropdownOpenDelayTimeout) {
              clearTimeout(this.#dropdownOpenDelayTimeout);
            }
            this.#dropdownOpenDelayTimeout = setTimeout(() => {
              this.classList.remove('header--transparent-dropdown-opening');
              this.#dropdownOpenDelayTimeout = null;
            }, 180);
          }
      }
    }
  }

  /**
   * True while any dropdown, mega menu, dropdown dialog or search bar is still open.
   * Reads the live open state, which the attribute lags during the close animation.
   */
  #hasOpenDropdown() {
    for (const details of this.querySelectorAll('details[is="menu-dropdown"], details[is="mega-dropdown"]')) {
      if (details.open) return true;
    }
    return !!this.querySelector('.dropdown-dialog-component .dialog[open], search-bar-component.search-bar--open');
  }

  /**
   * Handles dropdown close event
   */
  #handleDropdownClose = (event) => {

    if (this.headerTransparent) {
      this.#openDropdownCount = Math.max(0, this.#openDropdownCount - 1);
      // The count drifts if a dropdown is torn out while open, so let the DOM settle it
      if (this.#openDropdownCount > 0 && !this.#hasOpenDropdown()) this.#openDropdownCount = 0;

      if (this.#openDropdownCount === 0) {
        if (this.#dropdownCloseTimeout) {
          clearTimeout(this.#dropdownCloseTimeout);
        }
        const isMegaDropdown = event?.target?.matches?.('[is="mega-dropdown"]');
        if (isMegaDropdown) {
          this.#dropdownCloseTimeout = setTimeout(() => {
            if (this.querySelector('.menu__mega.mega-dropdown--closing')) {
              this.#dropdownCloseTimeout = null;
              return;
            }

            this.classList.remove('header--mega-menu-open', 'header--dropdown-menu-closing', 'header--mega-from-top');
            this.#dropdownCloseTimeout = null;
          }, 150);
          return;
        }

        this.#dropdownCloseTimeout = setTimeout(() => {
          if (this.querySelector('.menu__mega.mega-dropdown--closing')) {
            this.#dropdownCloseTimeout = null;
            return;
          }

          this.classList.add('header--dropdown-menu-closing');
          this.#dropdownCloseTimeout = setTimeout(() => {
            this.classList.remove('header--mega-menu-open', 'header--dropdown-menu-closing', 'header--mega-from-top');
            this.#dropdownCloseTimeout = null;
          }, 300);
        }, 100);
      }
    }

    if (this.#animateFastTimeout) {
      clearTimeout(this.#animateFastTimeout);
    }
    this.#animateFastTimeout = setTimeout(() => {
      this.classList.remove('header--open-dropdown-menu');
      this.#animateFastTimeout = null;
    }, 500);
  }

  get headerNavigationList() {
    return this.refs.headerNavigationList;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get stickyType() {
    return this.dataset.stickyType || 'none';
  }

  get headerOverlay() {
    return this.headerSection?.querySelector('.header-overlay');
  }

  get headerSection() {
    return this.closest('.header-section');
  }

  get announcementBarSection() {
    const previousSection = this.headerSection?.previousElementSibling;
    if (previousSection && previousSection.classList.contains('section-group-announcement-bar')) {
      return previousSection;
    }
    return null;
  }

  get headerTransparent() {
    return this.#isHeaderTransparent;
  }

  get headerDesktop() {
    return this.classList.contains('header__desktop');
  }

  get headerMobile() {
    return this.classList.contains('header__mobile');
  }
}

if(!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

/* <header-navigation> — lazy-loads mega menu markup via the Section Rendering API */
class HeaderNavigation extends Component {
  /**
   * Loads mega dropdown menu content via Section Rendering API
   * Uses /?view=menu-loader to fetch menu structure without polluting search analytics
   */
  handleMegaDropdownLoaded = () => {
    if (!this.hasAttribute('data-has-mega-menu')) return;
    // Custom Doussoux: load the menu in the visitor's language (/fr/, /en/…), not always the default one
    const url = new URL((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/', location.origin);
    url.searchParams.set('view', 'menu-loader');

    // The nav list is fully replaced. If the user already opened a dropdown while the
    // content was still loading, that element is destroyed, so remember its position and
    // re-open the matching new one afterwards — otherwise the menu collapses under the
    // cursor and stays blank until the next hover.
    const DROPDOWN = 'details[is="mega-dropdown"], details[is="menu-dropdown"]';
    const OPEN_DROPDOWN = 'details[is="mega-dropdown"][open], details[is="menu-dropdown"][open]';
    let openIndex = -1;

    sectionRenderer.fetchAndMorphContent({
      sectionId: this.sectionId,
      url: url,
      targetElement: this.headerNavigationList,
      sourceSelector: `[data-nav-id="${this.dataset.navId}"] [ref="headerNavigationList"]`,
      cache: false,
      componentElement: this,
      checkLoadingStates: true,
      onMorph: false,
      onBeforeRender: (list) => {
        const openItem = list.querySelector(OPEN_DROPDOWN)?.closest('.menu__item');
        if (openItem) openIndex = [...list.children].indexOf(openItem);
      },
      onSuccess: () => {
        if (openIndex < 0 || !this.matches(':hover')) return;
        // refs are stale after replaceWith, so read the freshly inserted list directly.
        const newList = this.querySelector('[ref="headerNavigationList"]');
        const dropdown = newList?.children[openIndex]?.querySelector(DROPDOWN);
        if (dropdown && !dropdown.open) {
          dropdown.beforeOpen();
          dropdown.open = true;
        }
      }
    });
  }

  get headerDesktop() {
    return this.closest('.header__desktop');
  }

  get headerNavigationList() {
    return this.refs.headerNavigationList;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }
}
if (!customElements.get('header-navigation')) {
  customElements.define('header-navigation', HeaderNavigation);
}

/* <details is="menu-dropdown"> — nav dropdown: hover/click open, focus & outside-close */
class MenuDropdown extends HTMLDetailsElement {
  static OPEN_DELAY = 100;
  static CLOSE_DELAY = 250;

  /** @type {MenuDropdown|null} */
  static #activeDropdown = null;

  /** @type {ReturnType<typeof setTimeout>|null} */
  #openTimer = null;
  /** @type {ReturnType<typeof setTimeout>|null} */
  #closeTimer = null;

  constructor() {
    super();

    this.summaryElement = this.firstElementChild;
    this.dropdownContent = this.summaryElement.nextElementSibling;

    this.summaryElement.addEventListener('click', this.#handleClick);
    if (this.action === 'hover') {
      this.addEventListener('mouseenter', this.#handleMouseEnter);
      this.addEventListener('mouseleave', this.#handleMouseLeave);
    }

    this.summaryElement.addEventListener('pointerdown', (e) => {
      this._mouseDown = true;
      this._lastPointerType = e.pointerType;
      requestAnimationFrame(() => { this._mouseDown = false; });
    });
    this.summaryElement.addEventListener('focusin', (event) => {
      if (this._mouseDown) return;
      if (event.target !== this.summaryElement) return;
      // Only auto-open from genuine keyboard focus. Mouse focus and window/tab
      // refocus (returning to the tab re-fires focusin on the still-focused
      // summary) are not :focus-visible, so they no longer wrongly reopen it.
      if (!this.summaryElement.matches(':focus-visible')) return;
      this.beforeOpen();
      this.open = true;
    });
    this.addEventListener('focusout', (event) => {
      if (!event.relatedTarget) return;
      if (this.contains(event.relatedTarget)) return;
      if (event.relatedTarget?.closest('[is="mega-dropdown"]')) return;
      this.open = false;
    });

    this._open = this.hasAttribute('open');
  }

  #handleMouseEnter = () => {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = null;

    clearTimeout(this.#openTimer);
    this.#openTimer = setTimeout(() => {
      this.#openTimer = null;
      if (MenuDropdown.#activeDropdown === this) return;
      this.#closeSiblingDropdowns();
      this.beforeOpen();
      this.open = true;
      MenuDropdown.#activeDropdown = this;
    }, MenuDropdown.OPEN_DELAY);
  };

  #handleMouseLeave = () => {
    clearTimeout(this.#openTimer);
    this.#openTimer = null;

    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = null;
      this.open = false;
      if (MenuDropdown.#activeDropdown === this) MenuDropdown.#activeDropdown = null;
    }, MenuDropdown.CLOSE_DELAY);
  };

  /** Hook for subclasses - called before open, when details is not yet open */
  beforeOpen() {}

  #closeSiblingDropdowns() {
    const list = this.parentElement?.closest('ul');
    if (!list) return;
    list.querySelectorAll(':scope > li > details[open]').forEach((d) => {
      if (d !== this) d.open = false;
    });
  }

  disconnectedCallback() {
    clearTimeout(this.#openTimer);
    this.#openTimer = null;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
    // Drop document-level keydown/click listeners if disconnected while open
    // (e.g. Section Rendering API morph or theme-editor reorder).
    this.removeCloseListeners();
    // A dropdown torn out while open never reaches hide(), so the header would count it
    // as open forever. A detached element cannot bubble, so announce it on the document.
    if (this._open) {
      this._open = false;
      if (MenuDropdown.#activeDropdown === this) MenuDropdown.#activeDropdown = null;
      document.dispatchEvent(new MenuDropdownCloseEvent());
    }
    super.disconnectedCallback?.();
  }

  get dropdownContentInner() {
    return this.dropdownContent?.firstElementChild;
  }

  get open() {
    return this._open;
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;
      if (!value && MenuDropdown.#activeDropdown === this) MenuDropdown.#activeDropdown = null;

      if (this.isConnected) {
        this.#handleTransition(value);
      } else {
        if (value) {
          this.setAttribute('open', '');
        } else {
          this.removeAttribute('open');
        }
      }
    }
  }

  /**
   * Handles click on dropdown summary
   * For hover mode on desktop: navigate to URL if present
   * Otherwise: toggle dropdown open/closed
   */
  #handleClick = (e) => {
    e.preventDefault();

    // On desktop with hover mode, clicking should navigate (not toggle)
    // Check pointer type of the actual interaction: mouse/pen = navigate, touch = toggle
    const isMousePointer = this._lastPointerType === 'mouse' || this._lastPointerType === 'pen';
    if (isMousePointer && this.action === 'hover' && this.summaryElement.hasAttribute('data-url')) {
      window.location.href = this.summaryElement.getAttribute('data-url');
    } else {
      if (!this.open) this.beforeOpen();
      this.open = !this.open;
    }
  }

  /**
   * Handles dropdown open/close transition animation
   * @param {boolean} value - True to open, false to close
   */
  #handleTransition(value) {
    if (value) {
      this.setAttribute('open', '');
      this.show();
      requestAnimationFrame(() => {
        this.#shouldReverse();
      });
    } else {
      this.hide();

    }
  }

  /**
   * Shows dropdown with animation and sets up event listeners
   */
  show() {
    this.classList.remove('menu-dropdown--animate', 'menu-dropdown--closing');
    requestAnimationFrame(() => {
      this.classList.add('menu-dropdown--animate');
    });
    this.#updateMenuHeight();
    this.addCloseListeners();
    this.dispatchEvent(new MenuDropdownOpenEvent(this));
  }

  /**
   * Hides dropdown with animation and removes event listeners
   */
  hide() {
    this.classList.remove('menu-dropdown--animate');
    this.classList.add('menu-dropdown--closing');
    if (this.dropdownContent) {
      onAnimationEnd(this.dropdownContent, () => {
        this.classList.remove('menu-dropdown--closing');
        if (!this._open) this.removeAttribute('open');
        if (!document.querySelector('.dialog[open]') && !document.querySelector('.search-bar--open') && !document.querySelector('details[is="mega-dropdown"][open]') && !document.querySelector('details[is="menu-dropdown"][open]')) {
          document.documentElement.removeAttribute('scroll-lock');
        }
      });
    }
    this.removeCloseListeners();
    this.dispatchEvent(new MenuDropdownCloseEvent());
    return false;
  }

  addCloseListeners() {
    document.addEventListener('keydown', this.#handleEscKeyPress);
    document.addEventListener('click', this.#handleClickOutside);
  }

  removeCloseListeners() {
    document.removeEventListener('click', this.#handleClickOutside);
    document.removeEventListener('keydown', this.#handleEscKeyPress);
  }

  /**
   * Closes dropdown when Escape key is pressed
   */
  #handleEscKeyPress = (e) => {
    if (e.code === 'Escape') {
      const currentDetails = e.target.closest('details[open]');
      if (currentDetails) {
        currentDetails.open = false;
      }
    }
  }



  /**
   * Closes dropdown when clicking outside
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.open = false;
    }
  };

  #updateMenuHeight() {
    const height = this.dropdownContentInner?.getBoundingClientRect().height ?? 0;
    this.style.setProperty('--menu-height', `${Math.round(height)}px`);
  }

  /**
   * Checks if dropdown should reverse direction to fit in viewport
   */
  #shouldReverse() {
    if (!this.dropdownContent || !this.summaryElement) return;

     // Reset previous state
    this.dropdownContent.classList.remove('reverse');
    this.dropdownContent.style.removeProperty('--dropdown-shift-x');

    const dropdownRect = this.dropdownContent.getBoundingClientRect();
    const shouldReverse = dropdownRect.right > window.innerWidth;

    if (!shouldReverse) return;

    if (this.level === '1') {
      const triggerRect = this.summaryElement.getBoundingClientRect();
      const shiftX = Math.floor(triggerRect.right - dropdownRect.right);
      this.dropdownContent.style.setProperty('--dropdown-shift-x', `${shiftX}px`);
    }
    this.dropdownContent.classList.add('reverse');
  }

  get offsetLeft() {
    return this.getBoundingClientRect().left;
  }

  get offsetTop() {
    return this.getBoundingClientRect().top;
  }

  get level() {
    return this.getAttribute('level');
  }

  get action() {
    return this.getAttribute('data-action');
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }
}
if (!customElements.get('menu-dropdown')) {
  customElements.define('menu-dropdown', MenuDropdown, { extends: 'details' });
}

/* <details is="mega-dropdown"> — full-width mega menu, overlay/cursor sync (extends MenuDropdown) */
class MegaDropdown extends MenuDropdown {
  #deselectTimeout = null;
  #closeTimeout = null;
  #animateTimeout = null;
  #resetMegaMenuHeightTimeout = null;
  static #borderRemoveTimeout = null;
  #abortController = new AbortController();
  #blockSelected = false;
  static #currentMegaMenu = null;

  // Cursor sync for the header background overlay (desktop mega menu)
  #cursorBinding = null;

  constructor() {
    super();

    // Only setup design mode handlers if in design mode
    if (!Shopify.designMode) {
      return;
    }

    const { signal } = this.#abortController;

    // Cancel timeout when another block in the same mega-dropdown is selected
    document.addEventListener('mega-dropdown:cancel-deselect', (event) => {
      if (event.detail?.megaDropdown === this && this.#deselectTimeout) {
        clearTimeout(this.#deselectTimeout);
        this.#deselectTimeout = null;
      }
    }, { signal });

    this.addEventListener('shopify:block:select', (event) => {
      if (this.contains(event.target)) {
        // Cancel timeout and notify same mega-dropdown instances
        if (this.#deselectTimeout) {
          clearTimeout(this.#deselectTimeout);
          this.#deselectTimeout = null;
        }

        document.dispatchEvent(new CustomEvent('mega-dropdown:cancel-deselect', {
          detail: { megaDropdown: this }
        }));

        this.#blockSelected = true;
        this.#openAfterReveal();
      }
    }, { signal });

    document.addEventListener('click', (event) => {
      if (!this.#blockSelected) return;
      const overlay = this.#getHeaderOverlay();
      if (overlay && overlay.contains(event.target)) {
        this.#blockSelected = false;
        this.open = false;
      }
    }, { signal });

    this.addEventListener('shopify:block:deselect', (event) => {
      if (this.contains(event.target)) {
        if (this.#deselectTimeout) {
          clearTimeout(this.#deselectTimeout);
        }

        this.#deselectTimeout = setTimeout(() => {
          if (this.#deselectTimeout) {
            this.#deselectTimeout = null;
            this.#blockSelected = false;
            this.open = false;
          }
        }, 100);
      }
    }, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#abortController.abort();
  }

  beforeOpen() {
    let delay = 0.2;
    const items = [
      ...this.querySelectorAll('.mega-menu__animate'),
      ...this.querySelectorAll('.mega-menu__product-list .product-card'),
    ];
    items.forEach((el) => {
      el.style.setProperty('--transition-delay', `${delay}s`);
      delay += 0.05;
    });

    // Safari: column-count miscalculates layout on first paint when children have transforms.
    // Force a synchronous reflow before the menu becomes visible to correct column positions.
    const list = this.querySelector('.mega-menu__list');
    if (list) void list.offsetHeight;
  }

  #getHeaderOverlay() {
    return this.closest('header-component')?.querySelector('.header-overlay') ?? null;
  }

  #getMegaMenuCursorContainer() {
    return this.querySelector('.menu__mega') ?? null;
  }

  #setupCursorOverlay() {
    if (this.action !== 'click') return;
    this._teardownCursorOverlay();

    const overlay = this.#getHeaderOverlay();
    const container = this.#getMegaMenuCursorContainer();
    if (!overlay || !container) return;

    this.#cursorBinding = bindCloseCursor({
      root: overlay,
      except: container,
      mask: overlay,
    });
  }

  _teardownCursorOverlay() {
    if (this.action !== 'click') return;
    this.#cursorBinding?.stop();
    this.#cursorBinding = null;
  }

  show() {
    this.cancelClose();

    this.querySelector('.menu__mega')?.classList.remove('mega-dropdown--closing');

    /** @type {MegaDropdown|null} */
    const prev = MegaDropdown.#currentMegaMenu;
    const prevMenuMega = prev?.querySelector('.menu__mega');
    if (prev && prev !== this) {
      prev.cancelClose();
      prev.removeCloseListeners();
      prev.classList.remove('mega-dropdown--animate');
      prev._open = false;
      prev.removeAttribute('open');
      prev.dispatchEvent(new MenuDropdownCloseEvent(prev));
      if (prevMenuMega) {
        void prevMenuMega.offsetHeight;
        prevMenuMega.classList.remove('mega-dropdown--overflow');
      }
      if (prev instanceof MegaDropdown) {
        prev._teardownCursorOverlay();
      }
    }

    this.dispatchEvent(new MenuDropdownOpenEvent(this));

    this.classList.remove('mega-dropdown--animate');
    this.#updateMegaMenuHeight();

    requestAnimationFrame(() => {
      this.classList.add('mega-dropdown--animate');
    });

    this.#openOverlay();
    this.addCloseListeners();

    this.#setupCursorOverlay();
    MegaDropdown.#currentMegaMenu = this;
    document.documentElement.setAttribute('scroll-lock', '');

    this.#setupMenuOverflow();
  }

  #setupMenuOverflow() {
    const menuMega = this.querySelector('.menu__mega');
    if (!menuMega) return;
    menuMega.classList.remove('mega-dropdown--overflow');
    const self = this;
    menuMega.addEventListener('transitionend', function onTransitionEnd(e) {
      if (e.propertyName !== 'height') return;
      menuMega.removeEventListener('transitionend', onTransitionEnd);
      if (!self._open) return;
      menuMega.classList.add('mega-dropdown--overflow');
    });
  }

  cancelClose() {
    clearTimeout(this.#closeTimeout);
    this.#closeTimeout = null;
    clearTimeout(this.#animateTimeout);
    this.#animateTimeout = null;
    clearTimeout(this.#resetMegaMenuHeightTimeout);
    this.#resetMegaMenuHeightTimeout = null;
    clearTimeout(MegaDropdown.#borderRemoveTimeout);
    MegaDropdown.#borderRemoveTimeout = null;
    this.querySelector('.menu__mega')?.classList.remove('mega-dropdown--closing');
  }

  hide() {
    // In design mode, keep the menu open while a block inside it is selected
    // so hovering away doesn't collapse the mega menu during editing
    if (this.#blockSelected) {
      this._open = true;
      return;
    }
    const menuMegaEl = this.querySelector('.menu__mega');
    menuMegaEl?.classList.remove('mega-dropdown--overflow');
    if (this.action === 'click') {
      this._teardownCursorOverlay();
    }
    this.removeCloseListeners();
    this.dispatchEvent(new MenuDropdownCloseEvent(this));
    this.#closeTimeout = setTimeout(() => {
      this.#closeTimeout = null;
      this.#closeOverlay();
      if (MegaDropdown.#currentMegaMenu === this) MegaDropdown.#currentMegaMenu = null;
      const menuMega = this.querySelector('.menu__mega');
      if (menuMega) void menuMega.offsetHeight;
      menuMega?.classList.add('mega-dropdown--closing');
      this.#resetMegaMenuHeight();
    }, 100);
    this.#animateTimeout = setTimeout(() => {
      this.#animateTimeout = null;
      this.classList.remove('mega-dropdown--animate');
      if (!this.open) this.removeAttribute('open');
      this.querySelector('.menu__mega')?.classList.remove('mega-dropdown--closing');
      if (!document.querySelector('.dialog[open]') && !document.querySelector('.search-bar--open') && !document.querySelector('details[is="menu-dropdown"][open]')) {
        document.documentElement.removeAttribute('scroll-lock');
      }
    }, 300);
  }

  #closeOverlay() {
    const overlay = this.#getHeaderOverlay();
    if (overlay) {
      overlay.classList.remove('opening');
      overlay.classList.add('closing');
      overlay.addEventListener('animationend', () => {
        overlay.classList.remove('closing');
      }, { once: true });
    }
  }

  #openOverlay() {
    const overlay = this.#getHeaderOverlay();
    overlay?.classList.remove('closing');
    overlay?.classList.add('opening');
  }

  /**
   * Opens the mega menu, revealing a collapsed sticky header in place via
   * header-pinning (no scroll). For top_only the hidden row animates its height,
   * so the open is deferred until that transition ends and #getRowOffset()
   * measures the real height, keeping --mega-menu-top correct.
   */
  #openAfterReveal() {
    const headerSection = this.closest('.header-section');
    const collapsed = headerSection?.classList.contains('header-scrolling')
      && !headerSection.classList.contains('header-pinning');

    if (!collapsed) {
      this.open = true;
      return;
    }

    headerSection.classList.add('header-pinning', 'header-revealed');

    const row = this.closest('.header__row');
    if (!headerSection.classList.contains('header-sticky--top_only') || !row) {
      this.open = true;
      return;
    }

    let opened = false;
    const finish = () => {
      if (opened) return;
      opened = true;
      row.removeEventListener('transitionend', onEnd);
      this.open = true;
    };
    const onEnd = (event) => {
      if (event.propertyName === 'height') finish();
    };
    row.addEventListener('transitionend', onEnd);
    setTimeout(finish, 400);
  }

  #getRowOffset() {
    const row = this.closest('.header__row');
    const headerDesktopEl = this.closest('.header__desktop');
    if (!row || !headerDesktopEl) return 0;
    return Math.round(row.getBoundingClientRect().bottom - headerDesktopEl.getBoundingClientRect().top);
  }

  #measureHeight() {
    const inner = this.querySelector('.mega-menu__inner');
    const contentHeight = inner?.offsetHeight ?? 0;
    const maxHeight = Math.max(0, window.innerHeight * 0.85 - this.#getRowOffset());
    this._cachedHeight = Math.round(Math.min(contentHeight, maxHeight));
  }

  #applyHeight() {
    const headerDesktopEl = this.closest('.header__desktop');
    if (!headerDesktopEl || !this._cachedHeight) return;
    headerDesktopEl.classList.add('header--mega-menu-open', 'header--mega-menu-active');
    if (headerDesktopEl.classList.contains('header-sticky--boxed')) {
      headerDesktopEl.classList.add('header--mega-menu-border');
    }
    headerDesktopEl.classList.remove('header--mega-menu-closing');
    headerDesktopEl.style.setProperty('--mega-menu-height', `${this._cachedHeight}px`);

    const row = this.closest('.header__row');
    if (row) {
      headerDesktopEl.style.setProperty('--mega-menu-top', `${this.#getRowOffset()}px`);
      headerDesktopEl.classList.toggle('header--mega-from-top', row.classList.contains('header__row--top'));
    }
  }

  #updateMegaMenuHeight() {
    if (this._cachedHeight) {
      this.#applyHeight();
    } else {
      setTimeout(() => {
        if (this._open) {
          this.#measureHeight();
          this.#applyHeight();
        }
      }, 100);
    }
  }

  #getOpenHeaderDropdown(headerDesktopEl) {
    return headerDesktopEl.querySelector('details[is="menu-dropdown"][open], .dropdown-dialog-component .dialog[open], search-bar-component.search-bar--open');
  }

  #resetHeightForOpenDropdown(headerDesktopEl) {
    headerDesktopEl.style.setProperty('--mega-menu-height', '0px');
    headerDesktopEl.classList.remove('header--mega-menu-active', 'header--mega-menu-closing');
    clearTimeout(MegaDropdown.#borderRemoveTimeout);
    MegaDropdown.#borderRemoveTimeout = setTimeout(() => {
      MegaDropdown.#borderRemoveTimeout = null;
        headerDesktopEl.classList.remove('header--mega-menu-border');
    }, 150);
  }

  #resetMegaMenuHeight() {
    const headerDesktopEl = this.closest('.header__desktop');
    if (!headerDesktopEl) return;

    if (this.#getOpenHeaderDropdown(headerDesktopEl)) {
      this.#resetHeightForOpenDropdown(headerDesktopEl);
      return;
    }

    headerDesktopEl.classList.add('header--mega-menu-closing');
    headerDesktopEl.classList.remove('header--mega-menu-active');

    clearTimeout(this.#resetMegaMenuHeightTimeout);
    this.#resetMegaMenuHeightTimeout = setTimeout(() => {
      this.#resetMegaMenuHeightTimeout = null;
      // If this dropdown was re-opened during the closing animation, restore state and bail
      if (this._open) {
        headerDesktopEl.classList.remove('header--mega-menu-closing');
        return;
      }
      if (this.#getOpenHeaderDropdown(headerDesktopEl)) {
        this.#resetHeightForOpenDropdown(headerDesktopEl);
        return;
      }

      headerDesktopEl.style.setProperty('--mega-menu-height', '0px');
      headerDesktopEl.classList.remove('header--mega-menu-open', 'header--mega-menu-active', 'header--mega-menu-border', 'header--mega-menu-closing', 'header--mega-from-top');
    }, 300);
  }
}

if (!customElements.get('mega-dropdown')) {
  customElements.define('mega-dropdown', MegaDropdown, { extends: 'details' });
}

/* <mobile-menu-component> — mobile menu dialog with lazy content load */
class MobileMenuComponent extends DialogComponent {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  openDialog() {
    this.handleMobileMenuLoaded();
    super.openDialog();
  }

  /**
   * Loads mobile menu content via Section Rendering API
   * Uses /?view=menu-loader to fetch menu structure without polluting search analytics
   * Handles template unwrapping if content is wrapped in template element
   */
  handleMobileMenuLoaded = () => {
    const liveTemplate = this.querySelector('[ref="dialogTemplate"]');
    const menuRoot = liveTemplate?.content ?? this;
    if (!menuRoot.querySelector('[ref="mobileMenuList"][data-has-mega-menu]')) return;
    // Prevent duplicate loading
    if (this.hasAttribute('data-mobile-menu-loaded')) return;
    if (this.hasAttribute('data-mobile-menu-loading')) return;
    this.setAttribute('data-mobile-menu-loading', '');
    // Custom Doussoux: load the menu in the visitor's language (/fr/, /en/…), not always the default one
    const url = new URL((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/', location.origin);
    url.searchParams.set('view', 'menu-loader');

    sectionRenderer.getSectionHTML(this.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          this.removeAttribute('data-mobile-menu-loading');
          return;
        }

        const parseHtml = new DOMParser().parseFromString(resultsMarkup, 'text/html');
        const responseTemplate = parseHtml.querySelector('[ref="dialogTemplate"]');
        const responseRoot = responseTemplate?.content ?? parseHtml.querySelector('[ref="dialogInner"]');
        if (!responseRoot) { this.removeAttribute('data-mobile-menu-loading'); return; }

        const newLists = responseRoot.querySelectorAll('[ref="mobileMenuList"]');
        if (!newLists.length) { this.removeAttribute('data-mobile-menu-loading'); return; }

        const liveTemplate = this.querySelector('[ref="dialogTemplate"]');

        newLists.forEach((newList) => {
          const listId = newList.dataset.listId;
          const selector = listId
            ? `[ref="mobileMenuList"][data-list-id="${listId}"]`
            : '[ref="mobileMenuList"]';
          const liveList = this.querySelector(selector);
          const templateList = liveTemplate?.content.querySelector(selector);

          if (liveList) morph(liveList, newList);
          if (templateList) morph(templateList, newList);
        });

        this.setAttribute('data-mobile-menu-loaded', '');
        this.removeAttribute('data-mobile-menu-loading');
      })
      .catch((error) => {
        this.removeAttribute('data-mobile-menu-loading');
        console.error(error);
      });
  }

  /**
   * Handles theme editor design mode
   * Opens/closes menu when section is selected/deselected
   */
  handleDesignMode() {
    const section = this.closest('.shopify-section');
    this.handleSectionSelect = (event) => {
      this.openDialog();
    };
    section.addEventListener('shopify:section:select', this.handleSectionSelect);

    this.handleSectionDeselect = (event) => {
      this.closeDialog();
    };
    section.addEventListener('shopify:section:deselect', this.handleSectionDeselect);

    // Reopen the drawer when a block inside it is selected — e.g. after closing
    // with the X button, where no section:select fires to reopen it.
    this.handleBlockSelect = (event) => {
      if (this.contains(event.target)) {
        this.openDialog();
      }
    };
    this.addEventListener('shopify:block:select', this.handleBlockSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    const section = this.closest('.shopify-section');
    if (section) {
      if (this.handleSectionSelect) {
        section.removeEventListener('shopify:section:select', this.handleSectionSelect);
      }
      if (this.handleSectionDeselect) {
        section.removeEventListener('shopify:section:deselect', this.handleSectionDeselect);
      }
    }
    if (this.handleBlockSelect) {
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }
  }

  /**
   * Closes all open mobile menu submenus when main dialog closes
   * Ensures clean state when reopening menu
   */
  #handleDialogClose = () => {
    const activeDetails = this.querySelectorAll('details[is="mobile-menu-details"][open]');

    activeDetails.forEach((details) => {
      details.open = false;
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    });
  }

  get dialogInner() {
    return this.refs.dialogInner;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }
}
if (!customElements.get('mobile-menu-component')) {
  customElements.define('mobile-menu-component', MobileMenuComponent);
}

/* <details is="mobile-menu-details"> — mobile submenu open/close with animation */
class MobileMenuDetails extends HTMLDetailsElement {
  constructor() {
    super();
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton?.addEventListener('click', this.onCloseButtonClick.bind(this));
    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', this.#handleDesignModeSelect.bind(this));
      this.addEventListener('shopify:block:deselect', this.#handleDesignModeDeselect.bind(this));
    }
  }

  /**
   * Opens submenu in theme editor when block is selected
   */
  #handleDesignModeSelect = (event) => {
    const currentSubmenu = event.target.closest('details[is="mobile-menu-details"]');
    if (currentSubmenu) {
      currentSubmenu.classList.add('no-animation');
      currentSubmenu.open = true;
    }
  }

  /**
   * Closes submenu in theme editor when block is deselected
   */
  #handleDesignModeDeselect = (event) => {
    const currentSubmenu = event.target.closest('details[is="mobile-menu-details"]');
    if (currentSubmenu) {
      currentSubmenu.open = false;
      currentSubmenu.classList.remove('no-animation');
    }
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('button');
  }

  /**
   * Handles summary click to toggle submenu
   */
  onSummaryClick(event) {
    // A linked title navigates; only the toggle (icon) opens the submenu.
    const link = event.target.closest('.mobile-menu__title-link');
    event.preventDefault();
    if (link) {
      window.location.href = link.href;
      return;
    }

    if (!this.open) {
      this.open = true;
      this.summary.setAttribute('aria-expanded', true);
      const submenu = this.querySelector(':scope > .mobile-menu-mega__submenu');
      if (submenu) {
        submenu.classList.add('animate-in');
        onAnimationEnd(submenu).then(() => submenu.classList.remove('animate-in'));
      }
    }
  }

  /**
   * Handles close button click with animation
   */
  async onCloseButtonClick() {
    this.classList.add('closing');

    await onAnimationEnd(this, undefined, {
      subtree: true,
    });

    this.open = false;
    this.summary.setAttribute('aria-expanded', false);
    this.classList.remove('closing');
    if (Shopify.designMode) {
      this.classList.remove('no-animation');
    }
  }
}
if (!customElements.get('mobile-menu-details')) {
  customElements.define('mobile-menu-details', MobileMenuDetails, { extends: 'details' });
}

/* <height-observer> — writes observed element height to CSS vars (incl. header sticky heights) */
class HeightObserver extends HTMLElement {
  #resizeObserver = null;
  #lastHeight = 0;
  #onResize = throttle((entries) => this.#updateHeight(entries));

  constructor() {
    super();
    this.#resizeObserver = new ResizeObserver(this.#onResize);
  }

  connectedCallback() {
    if (window.ResizeObserver) {
      this.#resizeObserver.observe(this);
      const height = this.getBoundingClientRect().height;
      if (height) this.#setHeight(height);
    }

  }

  refresh() {
    this.#lastHeight = 0;
    const height = this.getBoundingClientRect().height;
    if (height) this.#setHeight(height);
  }

  disconnectedCallback() {
    this.#onResize.cancel?.();
    this.#resizeObserver?.disconnect();
  }

  #setHeight(height) {
    if (!height) {
      this.#lastHeight = 0;
      return;
    }
    if (Math.abs(height - this.#lastHeight) < 1) return;
    this.#lastHeight = height;
    const variable = this.getAttribute('variable');
    if (!variable) return;
    const root = document.documentElement.style;
    if (variable === 'header') {
      let stickyHeight = height;

      const header = this.querySelector('.header-component');
      if (header && header.classList.contains('header-sticky--boxed')) {
        stickyHeight += 16;
      }

      const stickyHeightPx = `${stickyHeight.toFixed(2)}px`;
      const inAlwaysStickyHeader = Boolean(this.closest('.header-sticky--always'));
      const inTopOnlyStickyHeader = Boolean(this.closest('.header-sticky--top_only'));
      const inBottomOnlyStickyHeader = Boolean(this.closest('.header-sticky--bottom_only'));
      if (inAlwaysStickyHeader) {
        root.setProperty('--header-sticky-height', stickyHeightPx);
      } else if (inTopOnlyStickyHeader) {
        const topRowHeight = this.querySelector('.header__row--top')?.offsetHeight || 0;
        root.setProperty('--header-sticky-height', `${topRowHeight.toFixed(2)}px`);
      } else if (inBottomOnlyStickyHeader) {
        const bottomRowHeight = this.querySelector('.header__row--bottom')?.offsetHeight || 0;
        root.setProperty('--header-sticky-height', `${bottomRowHeight.toFixed(2)}px`);
      } else {
        root.removeProperty('--header-sticky-height');
      }

      const inScrollUpStickyHeader = Boolean(this.closest('.header-sticky--scroll_up'));
      if (inScrollUpStickyHeader) {
        root.setProperty('--header-scrollup-height', stickyHeightPx);
      } else {
        root.removeProperty('--header-scrollup-height');
      }
    }

    root.setProperty(`--${variable}-height`, `${height.toFixed(2)}px`);
  }

  /**
   * Updates height CSS variable when element size changes
   */
  #updateHeight(entries) {
    entries.forEach((entry) => {
      if (entry.target === this) {
        const height = entry.borderBoxSize
          ? entry.borderBoxSize.length > 0
            ? entry.borderBoxSize[0].blockSize
            : entry.borderBoxSize.blockSize
          : entry.target.getBoundingClientRect().height;

        this.#setHeight(height);
      }
    });
  }
}

if (!customElements.get('height-observer')) {
  customElements.define('height-observer', HeightObserver);
}